import React from 'react';
import { Chip } from '@mui/material';

/**
 * Reusable Status Chip Component
 * Displays status with consistent color coding across the app
 * 
 * @param {string} status - Status value (e.g., 'draft', 'sent', 'paid', etc.)
 * @param {string} label - Display label (optional, defaults to capitalized status)
 * @param {string} size - Chip size ('small' or 'medium')
 * @param {object} sx - Additional styles
 * @param {object} customConfig - Custom status configuration
 */

// Default status configurations used across the app
const defaultStatusConfig = {
  // Quotation statuses
  draft: { label: 'Quotation', bgcolor: '#6c757d', color: 'white' },
  sent: { label: 'Sent', bgcolor: '#17a2b8', color: 'white' },
  viewed: { label: 'Viewed', bgcolor: '#007bff', color: 'white' },
  accepted: { label: 'Accepted', bgcolor: '#28a745', color: 'white' },
  rejected: { label: 'Cancelled', bgcolor: '#dc3545', color: 'white' },
  expired: { label: 'Expired', bgcolor: '#ffc107', color: '#212529' },
  converted: { label: 'Sales Order', bgcolor: '#6f42c1', color: 'white' },
  
  // Customer statuses
  lead: { label: 'Lead', bgcolor: '#6c757d', color: 'white' },
  prospect: { label: 'Prospect', bgcolor: '#ffc107', color: '#212529' },
  customer: { label: 'Customer', bgcolor: '#28a745', color: 'white' },
  inactive: { label: 'Inactive', bgcolor: '#dc3545', color: 'white' },
  
  // Invoice statuses
  paid: { label: 'Paid', bgcolor: '#d1fae5', color: '#059669' },
  partial: { label: 'Partial', bgcolor: '#fef3c7', color: '#d97706' },
  overdue: { label: 'Overdue', bgcolor: '#fee2e2', color: '#dc2626' },
  cancelled: { label: 'Cancelled', bgcolor: '#f3f4f6', color: '#9ca3af' },
  refunded: { label: 'Refunded', bgcolor: '#e0e7ff', color: '#6366f1' },
  
  // Sales Order statuses
  confirmed: { label: 'Confirmed', bgcolor: '#dbeafe', color: '#2563eb' },
  processing: { label: 'Processing', bgcolor: '#fef3c7', color: '#d97706' },
  shipped: { label: 'Shipped', bgcolor: '#e0e7ff', color: '#6366f1' },
  delivered: { label: 'Delivered', bgcolor: '#d1fae5', color: '#059669' },
  
  // Activity statuses
  pending: { label: 'Pending', bgcolor: '#fef3c7', color: '#d97706' },
  completed: { label: 'Completed', bgcolor: '#d1fae5', color: '#059669' },
  
  // Priority levels
  low: { label: 'Low', bgcolor: '#d1fae5', color: '#10b981' },
  medium: { label: 'Medium', bgcolor: '#fef3c7', color: '#f59e0b' },
  high: { label: 'High', bgcolor: '#fed7aa', color: '#f97316' },
  urgent: { label: 'Urgent', bgcolor: '#fee2e2', color: '#ef4444' },

  // Generic statuses
  active: { label: 'Active', bgcolor: '#d1fae5', color: '#059669' },
  success: { label: 'Success', bgcolor: '#d1fae5', color: '#059669' },
  warning: { label: 'Warning', bgcolor: '#fef3c7', color: '#d97706' },
  error: { label: 'Error', bgcolor: '#fee2e2', color: '#dc2626' },
  info: { label: 'Info', bgcolor: '#dbeafe', color: '#2563eb' },
};

const StatusChip = ({
  status,
  label,
  size = 'small',
  sx = {},
  customConfig = {},
  variant = 'filled',
  onClick,
}) => {
  // Merge custom config with defaults
  const config = { ...defaultStatusConfig, ...customConfig };
  
  // Get status configuration
  const statusConfig = config[status?.toLowerCase()] || {
    label: status || 'Unknown',
    bgcolor: '#6c757d',
    color: 'white',
  };

  // Use provided label or config label
  const displayLabel = label || statusConfig.label;

  return (
    <Chip
      label={displayLabel}
      size={size}
      onClick={onClick}
      sx={{
        bgcolor: variant === 'filled' ? statusConfig.bgcolor : 'transparent',
        color: statusConfig.color,
        border: variant === 'outlined' ? `1px solid ${statusConfig.bgcolor}` : 'none',
        fontWeight: 500,
        fontSize: size === 'small' ? '11px' : '12px',
        height: size === 'small' ? 22 : 28,
        borderRadius: '4px',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          opacity: 0.9,
        } : {},
        ...sx,
      }}
    />
  );
};

// Export status configuration for external use
export { defaultStatusConfig };
export default StatusChip;
